import React from "react";
import styled, { css } from "styled-components";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import Title from "common/Typography/Title.tsx";
import ColorPicker, { type Color } from "antd/es/color-picker";
import AppTheme from "@shared/common/Themes/AppTheme.ts";
import DarkModeTheme from "@shared/common/Themes/DarkModeTheme.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { inputBox, newLookFieldStates } from "common/Inputs/styles.ts";
import LinkButton from "common/Buttons/LinkButton.tsx";
import { MOBILE_SEARCH } from "common/constants.ts";

interface ColorSelectorsProps {
  lightModePrimaryColor: string | null;
  setLightModePrimaryColor: (color: string | null) => void;
  darkModePrimaryColor: string | null;
  setDarkModePrimaryColor: (color: string | null) => void;
}

const ColorSelectors = ({
  lightModePrimaryColor,
  setLightModePrimaryColor,
  darkModePrimaryColor,
  setDarkModePrimaryColor,
}: ColorSelectorsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ASSET_MANAGER);

  return (
    <ColorItemsWrapper>
      <Item>
        <ItemTitle>
          {t("configuration.titles.light_mode_primary_color")}
        </ItemTitle>
        <ColorPickerControl>
          <ColorPicker
            value={lightModePrimaryColor || LIGHT_MODE_DEFAULT_PRIMARY_COLOR}
            onChange={(color) =>
              setLightModePrimaryColor(getOpaqueHexColor(color))
            }
            format="hex"
            disabledAlpha={true}
            disabledFormat={true}
            panelRender={(panel) => (
              <ColorPickerPanelContent>
                {panel}
                <ColorPickerPanelFooter>
                  <PanelResetColorButton
                    type="link"
                    disabled={!lightModePrimaryColor}
                    onClick={() => setLightModePrimaryColor(null)}
                  >
                    {t("configuration.buttons.return_to_default")}
                  </PanelResetColorButton>
                </ColorPickerPanelFooter>
              </ColorPickerPanelContent>
            )}
          >
            <ColorTriggerButton type="button">
              <ColorSwatch
                $color={
                  lightModePrimaryColor || LIGHT_MODE_DEFAULT_PRIMARY_COLOR
                }
              />
              <ColorTriggerText>
                {getColorDisplayText(
                  lightModePrimaryColor,
                  t("configuration.labels.default_color")
                )}
              </ColorTriggerText>
            </ColorTriggerButton>
          </ColorPicker>
        </ColorPickerControl>
      </Item>
      <Item>
        <ItemTitle>
          {t("configuration.titles.dark_mode_primary_color")}
        </ItemTitle>
        <ColorPickerControl>
          <ColorPicker
            value={darkModePrimaryColor || DARK_MODE_DEFAULT_PRIMARY_COLOR}
            onChange={(color) =>
              setDarkModePrimaryColor(getOpaqueHexColor(color))
            }
            format="hex"
            disabledAlpha={true}
            disabledFormat={true}
            panelRender={(panel) => (
              <ColorPickerPanelContent>
                {panel}
                <ColorPickerPanelFooter>
                  <PanelResetColorButton
                    type="link"
                    disabled={!darkModePrimaryColor}
                    onClick={() => setDarkModePrimaryColor(null)}
                  >
                    {t("configuration.buttons.return_to_default")}
                  </PanelResetColorButton>
                </ColorPickerPanelFooter>
              </ColorPickerPanelContent>
            )}
          >
            <ColorTriggerButton type="button">
              <ColorSwatch
                $color={darkModePrimaryColor || DARK_MODE_DEFAULT_PRIMARY_COLOR}
              />
              <ColorTriggerText>
                {getColorDisplayText(
                  darkModePrimaryColor,
                  t("configuration.labels.default_color")
                )}
              </ColorTriggerText>
            </ColorTriggerButton>
          </ColorPicker>
        </ColorPickerControl>
      </Item>
    </ColorItemsWrapper>
  );
};

export default ColorSelectors;

const LIGHT_MODE_DEFAULT_PRIMARY_COLOR = AppTheme.colors.primaryBlue;
const DARK_MODE_DEFAULT_PRIMARY_COLOR = DarkModeTheme.colors.primaryBlue;

const getOpaqueHexColor = (color: Color) =>
  `#${color.toHex().slice(0, 6).toUpperCase()}`;

const getColorDisplayText = (color: string | null, defaultText: string) =>
  color ? color.toUpperCase() : defaultText;

const Item = styled.div`
  width: 100%;
`;
const ItemTitle = styled(Title).attrs({ level: 4 })`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Field labels in the label role. */
      && {
        margin-bottom: 6px;
        gap: 2px;
        ${newLookType.label};
        color: ${getNewLookTokens(p.theme).secondary};
      }
    `}
`;
const ColorItemsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  width: 100%;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Same two columns and gutter as the rest of the form. */
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 16px;
      @media (max-width: ${MOBILE_SEARCH}) {
        grid-template-columns: minmax(0, 1fr);
      }
    `}
`;
const ColorPickerControl = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;
const ColorTriggerButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
  border-radius: 8px;
  background: ${(p) => p.theme.colors.cardBgColor};
  color: ${(p) => p.theme.colors.primaryGrey};
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover,
  &:focus-visible {
    border-color: ${(p) => p.theme.colors.primaryBlue};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${(p) => p.theme.colors.primaryBlue}33;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Reads as one of the form's fields (40px touch target on phones). */
      height: 32px;
      padding: 0 11px 0 6px;
      @media (max-width: 449px) {
        height: 40px;
        padding-left: 10px;
      }
      border-radius: ${p.theme.radii.control}px;
      font-variant-numeric: tabular-nums;
      ${newLookFieldStates};
    `}
`;
const ColorSwatch = styled.span<{ $color?: string }>`
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  border-radius: 4px;
  border: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
  background: ${(p) => p.$color || p.theme.colors.layoutBgColor};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      border-radius: 6px;
    `}
`;
const ColorTriggerText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ColorPickerPanelContent = styled.div<any>`
  display: flex;
  flex-direction: column;

  .ant-color-picker-input {
    .ant-input-affix-wrapper {
      ${inputBox};

      &:hover,
      &-focused {
        border-color: ${(p) => p.theme.colors.primaryBlue};
      }
      .ant-input-prefix {
        color: ${(p) => p.theme.colors.secondaryGrey} !important;
      }
    }
  }
`;
const ColorPickerPanelFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  border-top: ${(p) => p.theme.borders.lightest};
  margin: 12px -12px -8px -12px;
  padding: 4px 12px 0 0;
`;
const PanelResetColorButton = styled(LinkButton)`
  &&& {
    height: 32px;
    padding: 0 4px;
  }
`;
